import { withShopifyAppContext } from '../../resultsNavigation'
/** Renders the server's selected state and ordered sections; only tab interaction is local. */
import type { ShopifyStudyResultsView } from '@testfeed/shopify-contracts'
import { ShopifySegmentedFilterNav } from '../ShopifySegmentedFilterNav'
import { SectionFrame } from '../study-builder/SectionFrame'
import { ServerResultCards, ServerResultsSections } from '../results/ServerResultsSections'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'

type StatusView = Exclude<ShopifyStudyResultsView, { state: 'report' }>

function ResultsStatus({ view }: { view: StatusView }) {
  const shopify = useAppBridge()
  const { data } = view
  const showSpinner = view.state === 'running'
  const startedAt = data.startedAt ? new Date(data.startedAt).toLocaleString() : null
  // A stopped run's heading and message live in the page banner; an empty progress card would only repeat it.
  const hasCard =
    showSpinner || data.lines.length > 0 || data.progress !== null || Boolean(startedAt || data.supportUrl)

  return (
    <s-stack direction="block" gap="base">
      {hasCard && (
        <SectionFrame title={data.title} description="" badge={data.badge}>
          <s-stack direction="block" gap="small-200">
            {showSpinner && <s-spinner size="base" />}
            {data.lines.map((line, index) => (
              <s-paragraph key={index}>{line}</s-paragraph>
            ))}
            {data.progress !== null && <s-progress value={data.progress} max={100} accessibilityLabel={data.title} />}
            {startedAt && (
              <s-text color="subdued">
                {data.startedAtLabel} {startedAt}
              </s-text>
            )}
            {data.supportUrl && (
              <s-button
                href={withShopifyAppContext(data.supportUrl)}
                onClick={() => trackShopifyAction(shopify, 'open-support', () => undefined)}
              >
                {data.supportLabel}
              </s-button>
            )}
          </s-stack>
        </SectionFrame>
      )}
      <ServerResultCards cards={data.versions} layout="columns" />
    </s-stack>
  )
}

export function DirectStudyResultsBody({
  view,
  tab,
  onSelectTab,
}: {
  view: ShopifyStudyResultsView
  tab: string | null
  onSelectTab: (tab: string) => void
}) {
  if (view.state !== 'report') return <ResultsStatus view={view} />
  const activeTab = view.tabs.find((item) => item.id === tab) ?? view.tabs.find((item) => item.id === view.defaultTabId)
  if (!activeTab) throw new Error('The validated results view has no default tab.')
  return (
    <s-stack direction="block" gap="base">
      <ShopifySegmentedFilterNav
        filters={view.tabs.map((item) => ({ id: item.id, label: item.label }))}
        selected={activeTab.id}
        onSelect={onSelectTab}
      />
      <ServerResultsSections sections={activeTab.sections} onSelectTab={onSelectTab} />
    </s-stack>
  )
}
